import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import {
  ContactCommunicationDirectionSchema,
  ContactCommunicationTypeSchema,
  ContactSourceSchema,
  ContactTypeDiscriminatorSchema,
} from '../relationship/contacts.schemas';

export const ContactCreatedPayloadSchema = z.object({
  contactId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  contactType: ContactTypeDiscriminatorSchema,
  source: ContactSourceSchema,
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type ContactCreatedPayload = z.infer<typeof ContactCreatedPayloadSchema>;

const ContactMutationPayloadSchema = z.object({
  contactId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export const ContactProfileUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactProfileUpdatedPayload = z.infer<typeof ContactProfileUpdatedPayloadSchema>;

export const ContactTypesUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactTypesUpdatedPayload = z.infer<typeof ContactTypesUpdatedPayloadSchema>;

export const ContactEmailsUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactEmailsUpdatedPayload = z.infer<typeof ContactEmailsUpdatedPayloadSchema>;

export const ContactPhonesUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactPhonesUpdatedPayload = z.infer<typeof ContactPhonesUpdatedPayloadSchema>;

export const ContactAddressUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactAddressUpdatedPayload = z.infer<typeof ContactAddressUpdatedPayloadSchema>;

export const ContactWarningNoteUpdatedPayloadSchema = ContactMutationPayloadSchema;
export type ContactWarningNoteUpdatedPayload = z.infer<typeof ContactWarningNoteUpdatedPayloadSchema>;

export const ContactDeletedPayloadSchema = ContactMutationPayloadSchema;
export type ContactDeletedPayload = z.infer<typeof ContactDeletedPayloadSchema>;

// A note recorded against a contact. Carries the note id (the projection's
// idempotency key) and the rendered text + when it occurred, so the activity
// timeline can project a `Notiz` entry without reading back into this module.
// Optional `propertyId` / `dealId` link the note to additional records so it
// appears on those timelines too (ADR 0025 §activity_link).
export const ContactNoteRecordedPayloadSchema = ContactMutationPayloadSchema.extend({
  noteId: z.uuid(),
  body: z.string(),
  propertyId: z.uuid().nullable().default(null),
  dealId: z.uuid().nullable().default(null),
});
export type ContactNoteRecordedPayload = z.infer<typeof ContactNoteRecordedPayloadSchema>;

// A communication recorded against a contact. Carries the full rendered shape
// (type / direction / text / subject / duration) so the activity timeline can
// project a direction-aware entry without reading back into this module.
// Optional `propertyId` / `dealId` link the comm to additional records (ADR 0025).
export const ContactCommunicationRecordedPayloadSchema = ContactMutationPayloadSchema.extend({
  communicationId: z.uuid(),
  type: ContactCommunicationTypeSchema,
  direction: ContactCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  propertyId: z.uuid().nullable().default(null),
  dealId: z.uuid().nullable().default(null),
});
export type ContactCommunicationRecordedPayload = z.infer<typeof ContactCommunicationRecordedPayloadSchema>;

// An edit to a recorded note / communication. Carries the full rendered shape
// so the activity timeline can amend the projected entry (a new content version
// marks it "edited") and re-sort it by `occurredAt`.
export const ContactNoteUpdatedPayloadSchema = ContactMutationPayloadSchema.extend({
  noteId: z.uuid(),
  body: z.string(),
});
export type ContactNoteUpdatedPayload = z.infer<typeof ContactNoteUpdatedPayloadSchema>;

export const ContactCommunicationUpdatedPayloadSchema = ContactMutationPayloadSchema.extend({
  communicationId: z.uuid(),
  type: ContactCommunicationTypeSchema,
  direction: ContactCommunicationDirectionSchema,
  content: z.string(),
  subject: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
});
export type ContactCommunicationUpdatedPayload = z.infer<typeof ContactCommunicationUpdatedPayloadSchema>;

export const ContactNoteDeletedPayloadSchema = ContactMutationPayloadSchema.extend({ noteId: z.uuid() });
export type ContactNoteDeletedPayload = z.infer<typeof ContactNoteDeletedPayloadSchema>;

export const ContactCommunicationDeletedPayloadSchema = ContactMutationPayloadSchema.extend({
  communicationId: z.uuid(),
});
export type ContactCommunicationDeletedPayload = z.infer<typeof ContactCommunicationDeletedPayloadSchema>;
